<script setup lang="ts">
import SearchComponent from '@/components/SearchComponent.vue'
import NotFound from '@/assets/not_found.svg'
import { ref } from 'vue'
import CustomButton from '@/components/CustomComponent/CustomButton.vue'
import type { SelectModel } from '@/models/SelectModel'
import type { SearchedLoan } from '@/api/models/loan/SearchedLoan'
import { useToast } from 'primevue/usetoast'
import type { ToastMessageOptions } from 'primevue/toast'
import type { SearchLoanRequest } from '@/api/models/loan/SearchLoanRequest'
import ApiService from '@/api/ApiService'
import router from '@/router'
import Storage from '@/utils/Storage'
import LoadingComponent from '@/components/LoadingComponent.vue'
import ConversionUtility from '@/utils/ConversionUtility'
import type { EMIPayment } from '@/api/models/loan/EMIPayment'
import type { AmortizationEMISchedule } from '@/models/AmortizationEMISchedule'
import { InterestAndTotalCalculator } from '@/utils/InterestAndTotalCalculator'
import DateUtils from '@/utils/DateUtils'
import type { PayEMIRequest } from '@/api/models/loan/PayEMIRequest'
import type { EMITransactionsRequest } from '@/api/models/loan/EMITransactionsRequest'
import type { CreatedBy } from '@/api/models/CreatedBy'
import type CloseLoanRequest from '@/api/models/loan/CloseLoanRequest'
import { useConfirm } from 'primevue/useconfirm'
import calculateDailyPendingInterest from '@/utils/CalculateDailyPendingInterest'

const loan = ref<SearchedLoan>()
const toast = useToast()
const confirmDialog = useConfirm()
const isLoanDetailsVisible = ref<boolean>(false)
const isLoanTransactionsVisible = ref<boolean>(false)
const isPayEMIVisible = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const emiTransactions = ref<EMIPayment[]>([])
const lastEmi = ref(false)

const payPrincialAmount = ref<number>()
const payInterestAmount = ref<number>()
const today = new Date()

const minDate = new Date(2024, 8, 1);

const maxDate = new Date(today)
maxDate.setMonth(maxDate.getMonth() + 3)

const payDate = ref<Date | null>(new Date())
//EMI variables
const emiAmount = ref<number>()
const voucherNo = ref<number>()
const penaltyAmount = ref<number>()
const lumpSumAmount = ref<number>()
const paymentOptions = ref<SelectModel[]>([
  { value: 'EMI', label: 'EMI' },
  { value: 'Lump Sum', label: 'Lump Sum' }
])
const selectedPaymentOption = ref<SelectModel>(paymentOptions.value[0])
const emiSchedule = ref<AmortizationEMISchedule[]>([])
const emiNo = ref<number>()

function calculateDailyInterest() {
  console.log("here")
  if (!loan.value) return
  payInterestAmount.value = calculateDailyPendingInterest(loan.value, payDate.value?.getTime() ?? new Date().getTime())
}

function handelLoanSearch(query: string) {
  loan.value = undefined
  isLoading.value = false
  isLoanDetailsVisible.value = false
  isLoanTransactionsVisible.value = false
  isPayEMIVisible.value = false
  emiSchedule.value = []
  emiNo.value = undefined
  emiTransactions.value = []
  isLoanTransactionsVisible.value = false
  if (!query) {
    showToast('Invalid search query', 'Please provide search details of the loan', 'error')
    return
  }
  if (!(query.length == 6)) {
    showToast('Invalid search query', 'Loan ID is at least 6 characters long', 'error')
    return
  }
  isLoading.value = true
  const searchLoan: SearchLoanRequest = { loanId: query }
  ApiService.searchLoan(searchLoan)
    .then((response) => {
      setTimeout(() => {
        if (response.statusCode == 401) {
          Storage.clearData()
          router.push({
            name: 'login'
          })
        }
      }, 2000)
      if (response.statusCode == 200) {
        loan.value = response.data
        emiSchedule.value = InterestAndTotalCalculator.generateAmortizationSchedule(
          loan.value.loanAmount!!,
          loan.value.interestRate!!,
          loan.value.tenureInMonths
        )
        emiNo.value =
          DateUtils.monthDifference(
            new Date(loan.value.loanStartDate),
            new Date(loan.value.nextDueDate)
          ) - (new Date(loan.value.loanStartDate).getDate() <= loan.value.emiDate ? 1 : 0)
        emiAmount.value = ConversionUtility.toRuppesCoversion(loan.value.emiAmount!!)
      } else {
        console.log(response)
        showToast('Failed to fetch Loans', response.error ?? 'Something is wrong!', 'error')
      }
    })
    .catch((error) => {
      showToast('Failed to fetch customers', error, 'error')
    })
    .finally(() => {
      isLoading.value = false
    })
}

function payEMI() {
  if (!payPrincialAmount.value && !payInterestAmount.value) {
    showToast('Invalid principal / interest amount', 'Please select a valid principal / interest amount.', 'error')
    return
  }
  if (!payDate.value) {
    showToast('Invalid date', 'Please select a valid date.', 'error')
    return
  }
  if (!loan.value) {
    showToast('Invalid loan', 'Please select a valid loan', 'error')
    return
  }
  if (!emiAmount.value && selectedPaymentOption.value.value == 'EMI') {
    showToast('EMI Amount is empty/blank', 'Enter a valid EMI amount', 'error')
    return
  }
  if (!voucherNo.value) {
    showToast('Voucher Number is empty/blank', 'Enter a valid voucher number', 'error')
    return
  }
  if (voucherNo.value.toString().length > 8) {
    showToast('Voucher Number is invalid', 'Voucher no should be less than 8 digits', 'error')
    return
  }
  if (!lumpSumAmount.value && selectedPaymentOption.value.value == 'Lump Sum') {
    showToast('Lump Sum Amount is empty/blank', 'Enter a valid lump sum amount', 'error')
    return
  }
  if (penaltyAmount.value) {
    if (
      ConversionUtility.toPaiseConversion(Number(penaltyAmount.value)) >
      Number(loan.value.emiAmount!!)
    ) {
      showToast(
        'Penalty Amount should be less than EMI Amount',
        'Enter a valid penalty amount',
        'error'
      )
      return
    }
  }

  lastEmi.value =
    loan.value.emiTransactions && loan.value.emiTransactions.length == loan.value.tenureInMonths - 1
  const createdBy: CreatedBy = {
    empId: Storage.getEmpId()!!,
    createdAt: new Date().getTime()
  }

  const EMIPayment: EMIPayment = {
    createdAt: payDate.value.getTime(),
    principalPaid: ConversionUtility.toPaiseConversion(payPrincialAmount.value ?? 0),
    interestPaid: ConversionUtility.toPaiseConversion(payInterestAmount.value ?? 0),
    penaltyCharges: ConversionUtility.toPaiseConversion(penaltyAmount.value ?? 0),
    voucherNumber: voucherNo.value,
    dueDate: DateUtils.emiDate(loan.value.emiDate, emiNo.value!! + 2, loan.value.loanStartDate),
    paymentStatus: 'PAID',
    createdBy: createdBy
  }
  const EMI: PayEMIRequest = {
    loanId: loan.value.loanId,
    nextEmiDate: DateUtils.emiDate(loan.value.emiDate, emiNo.value!! + 3, loan.value.loanStartDate),
    emiPayment: EMIPayment
  }
  isLoading.value = true
  ApiService.payEMI(EMI)
    .then((response) => {
      setTimeout(() => {
        if (response.statusCode == 401) {
          Storage.clearData()
          router.push({
            name: 'login'
          })
        }
      }, 2000)
      if (response.statusCode == 200) {
        showToast('EMI paid successfully', 'EMI paid successfully', 'success')
        if (loan.value?.loanId) {
          handelLoanSearch(loan.value?.loanId!!.toString())
        }
      } else {
        showToast('Failed to pay loan', response.data ?? 'Something is wrong!', 'error')
      }
    })
    .catch((error) => {
      showToast('Failed to pay loan', error, 'error')
    })
    .finally(() => {
      isLoading.value = false
      payPrincialAmount.value = undefined
      payInterestAmount.value = undefined
      payDate.value = new Date()
    })
}

function getEmiTransactions() {
  if (!loan.value) {
    showToast('Invalid loan', 'Please select a valid loan', 'error')
    return
  }
  isLoading.value = true
  const getEmiTransactions: EMITransactionsRequest = { loanId: loan.value.loanId.toString() }
  ApiService.getEMITransactions(getEmiTransactions)
    .then((response) => {
      setTimeout(() => {
        if (response.statusCode == 401) {
          Storage.clearData()
          router.push({
            name: 'login'
          })
        }
      }, 2000)
      if (response.statusCode == 200) {
        emiTransactions.value = response.data
      } else {
        isLoanTransactionsVisible.value = false
        showToast(
          'Failed to fetch EMI transactions',
          response.error ?? 'Something went wrong!',
          'error'
        )
      }
    })
    .catch((error) => {
      showToast('Failed to fetch EMI transactions', error, 'error')
    })
    .finally(() => {
      isLoading.value = false
    })
}

function closeLoan() {
  if (loan.value?.loanStatus == 'CLOSED') {
    showToast('Cannot close Loan', 'Loan account already closed', 'error')
    return
  }

  confirmDialog.require({
    message: `Are you sure you want to close this Loan account?`,
    header: 'Confirmation to close',
    icon: 'fas fa-exclamation-triangle',
    rejectProps: {
      label: 'Cancel',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Yes, close Loan Account'
    },
    accept() {
      const request: CloseLoanRequest = {
        loanId: Number(loan.value?.loanId!),
        createdAt: new Date().getTime()
      }
      isLoading.value = true
      ApiService.closeLoan(request)
        .then((response) => {
          setTimeout(() => {
            if (response.statusCode == 401) {
              Storage.clearData()
              router.push({
                name: 'login'
              })
            }
          }, 2000)
          if (response.statusCode == 200) {
            showToast('Loan Closed', response.data!, 'success')
            if (loan.value?.loanId) {
              handelLoanSearch(loan.value?.loanId!!.toString())
            }
          } else {
            showToast('Failed to close loan', response.error!, 'error')
          }
        })
        .catch((error) => {
          showToast('Failed to Close loan', error, 'error')
        })
        .finally(() => {
          isLoading.value = false
        })
    },
    reject() {}
  })
}

function showToast(summary: string, detail: string, severity: ToastMessageOptions['severity']) {
  toast.add({
    severity: severity,
    summary: summary,
    detail: detail,
    life: 5000
  })
}
</script>
<template>
  <main class="main">
    <Card class="search-card">
      <template #content>
        <Toast class="error-p-toast" />
        <SearchComponent
          title="Search Loans"
          subtitle="Search for Loans using Loan ID"
          placeholder="Search using Loan ID"
          @search-click="handelLoanSearch"
        />
        <div v-if="!loan && !isLoading" class="notFound">
          <Image :src="NotFound" width="800" />
          <Chip label="No accounts found for the provided search query!" style="font-weight: 600" />
        </div>
        <div v-if="isLoading">
          <LoadingComponent
            text="Please wait while we process the request!"
            :dialogVisiblity="isLoading"
          />
        </div>
        <div v-if="loan && !isLoading" class="cont">
          <div class="holder-details">
            <div>
              <span class="text-heading">Customer ID: </span>
              <span class="text">{{ loan.customerId }}<br /></span>
            </div>
            <div>
              <span class="text-heading">Customer Name: </span>
              <span class="text">{{ loan.firstName }} {{loan.middleName ?? ""}} {{loan.lastName}}<br /></span>
            </div>
            <!-- loan end date is not correct from backend so we hid end date and tenures. -->
            <!-- <div>
              <span class="text-heading">Tenure: </span>
              <span class="text">{{ ConversionUtility.toDateFormat(loan.loanEndDate) }}</span>
            </div> -->
            <!--            <div>-->
            <!--              <span class="text-heading">Next EMI Date: </span>-->
            <!--              <span class="text">{{ ConversionUtility.toDateFormat(loan.nextDueDate) }}</span>-->
            <!--            </div>-->
            <div>
              <span class="text-heading">Loan Amount: </span>
              <span class="text"
                >{{ ConversionUtility.toStringRupeesConversion(loan.loanAmount) }}<br
              /></span>
            </div>
            <div>
              <span class="text-heading">Started On: </span>
              <span class="text">{{ ConversionUtility.toDateFormat(loan.loanStartDate) }}</span>
            </div>
            <div>
              <span class="text-heading">Status: </span>
              <Tag :severity="loan.loanStatus == 'ACTIVE' ? 'success' : 'warn'" class="text"
                >{{ loan.loanStatus }}<br
              /></Tag>
            </div>
          </div>
          <div class="operations">
            <div class="button-transactions">
              <CustomButton
                title="View Loan Transcation"
                icon="book"
                @click="
                  () => {
                    isLoanTransactionsVisible = !isLoanTransactionsVisible
                    isLoanDetailsVisible = false
                    isPayEMIVisible = false
                    getEmiTransactions()
                  }
                "
              />
              <CustomButton
                title="View Loan Details"
                icon="angle-double-down"
                @click="
                  () => {
                    isLoanDetailsVisible = !isLoanDetailsVisible
                    isLoanTransactionsVisible = false
                    isPayEMIVisible = false
                  }
                "
              />
              <CustomButton
                title="Pay Loan"
                icon="indian-rupee"
                :outlined="true"
                @click="
                  () => {
                    voucherNo = undefined
                    penaltyAmount = undefined
                    isPayEMIVisible = !isPayEMIVisible
                    payInterestAmount = calculateDailyPendingInterest(loan, payDate?.getTime() ?? new Date().getTime())
                    isLoanDetailsVisible = false
                    isLoanTransactionsVisible = false
                  }
                "
              />
            </div>
            <div class="flex gap-3">
              <CustomButton
                title="Close Loan"
                severity="danger"
                icon="ban"
                @click="closeLoan"
                :disabled="loan.loanStatus != 'ACTIVE'"
              />
            </div>
          </div>
        </div>
        <Transition>
          <div v-if="isLoanTransactionsVisible">
            <div class="cont">
              <DataTable
                :value="emiTransactions"
                :paginator="true"
                :rows="10"
                :rowsPerPageOptions="[10, 25, 50]"
              >
                <Column header="Paid On">
                  <template #body="slotProps">
                    {{ ConversionUtility.toDateFormat(slotProps.data.createdAt) }}
                  </template>
                </Column>
                <Column header="Principal Paid">
                  <template #body="slotProps">
                    {{ ConversionUtility.toStringRupeesConversion(slotProps.data.principalPaid) }}
                  </template>
                </Column>
                <Column header="Interest Paid">
                  <template #body="slotProps">
                    {{ ConversionUtility.toStringRupeesConversion(slotProps.data.interestPaid) }}
                  </template>
                </Column>
                <Column header="Penalty">
                  <template #body="slotProps">
                    {{ ConversionUtility.toStringRupeesConversion(slotProps.data.penaltyCharges) }}
                  </template>
                </Column>
                <Column header="Total">
                  <template #body="slotProps">
                    {{
                      ConversionUtility.toStringRupeesConversion(
                        slotProps.data.interestPaid +
                          slotProps.data.principalPaid +
                          slotProps.data.penaltyCharges
                      )
                    }}
                  </template>
                </Column>
                <Column header="Voucher No">
                  <template #body="slotProps">
                    {{ slotProps.data.voucherNumber }}
                  </template>
                </Column>
                <Column header="Due On">
                  <template #body="slotProps">
                    {{ ConversionUtility.toDateFormat(slotProps.data.dueDate) }}
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </Transition>
        <Transition>
          <div v-if="isLoanDetailsVisible" class="cont">
            <div class="details-container">
              <Panel header="Loan Details">
                <Divider style="margin: 0" />
                <div class="details pt-5">
                  <div class="item">
                    <span class="text-heading">Customer ID: </span>
                    <span class="loan-text">{{ loan?.customerId }}<br /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Application ID: </span>
                    <span class="loan-text">{{ loan?.applicationId }}<br /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">ID: </span>
                    <span class="loan-text">{{ loan?.loanId }}<br /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Type: </span>
                    <span class="loan-text">{{ loan?.loanType }}<br /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Start Date: </span>
                    <span class="loan-text"
                      >{{ ConversionUtility.toDateFormat(loan?.loanStartDate ?? 0) }}<br
                    /></span>
                  </div>
                  <!-- <div>
                    <span class="text-heading">End Date: </span>
                    <span class="text"
                      >{{ ConversionUtility.toDateFormat(loan?.loanEndDate ?? 0) }}<br
                    /></span>
                  </div> -->
                  <div class="item">
                    <span class="text-heading">Tenure: </span>
                    <span class="loan-text"
                      >{{
                        (loan?.tenureInMonths ?? 0) > 12
                          ? Math.floor(loan?.tenureInMonths!! / 12) +
                            ' Years ' +
                            (loan?.tenureInMonths!! % 12 > 0
                              ? (loan?.tenureInMonths!! % 12) + ' Months'
                              : '')
                          : loan?.tenureInMonths + ' Months'
                      }}<br
                    /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Interest Rate: </span>
                    <span class="loan-text">{{ loan?.interestRate }} %<br /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Collateral Details: </span>
                    <span class="loan-text">{{ loan?.collateralDetails }}<br /></span>
                  </div>
                </div>
              </Panel>
              <Panel header="Amount Details">
                <Divider style="margin: 0" />
                <div class="grid grid-cols-3 gap-[1rem] pt-5">
                  <div class="item">
                    <span class="text-heading">Principal Amount: </span>
                    <span class="loan-text"
                      >{{ ConversionUtility.toStringRupeesConversion(loan?.loanAmount ?? 0) }}<br
                    /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Interest Amount: </span>
                    <span class="loan-text"
                      >{{ ConversionUtility.toStringRupeesConversion(loan?.interestAmount ?? 0)
                      }}<br
                    /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Outstanding Amount: </span>
                    <span class="loan-text"
                      >{{ ConversionUtility.toStringRupeesConversion(loan?.outstandingAmount ?? 0)
                      }}<br
                    /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Remaining Principal Amount: </span>
                    <span class="loan-text"
                      >{{
                        ConversionUtility.toStringRupeesConversion(
                          loan?.remainingPrincipalAmount ?? 0
                        )
                      }}<br
                    /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Remaining Interest Amount: </span>
                    <span class="loan-text"
                    >{{
                        ConversionUtility.toStringRupeesConversion(
                          loan?.remainingInterestAmount ?? 0
                        )
                      }}<br
                      /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Total Interest Paid: </span>
                    <span class="loan-text"
                      >{{
                        ConversionUtility.toStringRupeesConversion(
                          loan!.interestAmount - loan!.remainingInterestAmount
                        )
                      }}<br
                    /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Total Principal Paid: </span>
                    <span class="loan-text"
                    >{{
                        ConversionUtility.toStringRupeesConversion(
                          loan!.loanAmount - loan!.remainingPrincipalAmount
                        )
                      }}<br
                      /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Total Paid: </span>
                    <span class="loan-text"
                      >{{ ConversionUtility.toStringRupeesConversion(loan?.totalPaid ?? 0) }}<br
                    /></span>
                  </div>
                </div>
              </Panel>
              <Panel header="Bank Details" class="my-5">
                <Divider style="margin: 0" />
                <div class="flex flex-col gap-5 p-5">
                  <div class="item">
                    <span class="text-heading">Branch Code: </span>
                    <span class="loan-text">{{ loan?.branchCode }}<br /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Created On: </span>
                    <span class="loan-text"
                      >{{ ConversionUtility.toDateAndTimeFormat(loan?.createdAt ?? 0) }}<br
                    /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Updated On: </span>
                    <span class="loan-text"
                      >{{ ConversionUtility.toDateAndTimeFormat(loan?.updateAt ?? 0) }}<br
                    /></span>
                  </div>
                </div>
              </Panel>
              <!--
              <Panel header="EMI Details" class="my-5">
                <Divider style="margin: 0" />
                <div class="flex flex-col gap-5 p-5">
                  <div class="item">
                    <span class="text-heading">EMI Date: </span>
                    <span class="loan-text"
                      >{{
                        loan?.emiDate == 1
                          ? loan.emiDate + ' st'
                          : loan?.emiDate == 2
                            ? loan.emiDate + ' nd'
                            : loan?.emiDate == 3
                              ? loan.emiDate + ' rd'
                              : loan?.emiDate + ' th  '
                      }}<br
                    /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">Next EMI On: </span>
                    <span class="loan-text"
                      >{{
                        loan?.emiTransactions &&
                        loan?.emiTransactions.length == loan?.tenureInMonths
                          ? 'No EMI left. All are paid'
                          : ConversionUtility.toDateFormat(loan?.nextDueDate ?? 0)
                      }}<br
                    /></span>
                  </div>
                  <div class="item">
                    <span class="text-heading">EMI: </span>
                    <span class="loan-text"
                      >{{
                        loan?.emiTransactions &&
                        loan?.emiTransactions.length == loan?.tenureInMonths
                          ? 0
                          : ConversionUtility.toStringRupeesConversion(loan?.emiAmount ?? 0)
                      }}<br
                    /></span>
                  </div>
                </div>
              </Panel>
              -->
            </div>
          </div>
        </Transition>
        <Transition>
          <div v-if="isPayEMIVisible" class="cont">
            <h1 class="text-2xl font-bold flex justify-start pl-5 pb-2">Pay Loan</h1>
            <Divider style="margin: 0" />
            <!-- <SelectButton
              class="m-5"
              :options="paymentOptions"
              optionLabel="label"
              v-model="selectedPaymentOption"
              :allowEmpty="false"
            ></SelectButton> -->
            <div class="operations flex gap-5 mt-5">
              <div class="w-[90%] flex flex-row gap-5">
                <InputNumber
                  v-model="payPrincialAmount"
                  :min="0"
                  placeholder="Enter Principal Amount"
                  locale="en-IN"
                  class="input-text"
                  prefix="₹ "
                ></InputNumber>
                <InputNumber
                  v-model="payInterestAmount"
                  :min="0"
                  placeholder="Enter Interest Amount"
                  locale="en-IN"
                  class="input-text"
                  prefix="₹ "
                ></InputNumber>
                <InputNumber
                  v-model="penaltyAmount"
                  :min="0"
                  placeholder="Enter Penalty Amount"
                  locale="en-IN"
                  class="input-text"
                  prefix="₹ "
                ></InputNumber>
                <DatePicker
                  v-model="payDate"
                  :minDate="minDate"
                  :maxDate="maxDate"
                  date-format="dd/mm/yy"
                  class="input-text"
                  @update:modelValue="calculateDailyInterest"
                  showIcon
                />
                <InputNumber
                  v-model="voucherNo"
                  :min="1"
                  placeholder="Enter voucher number"
                  class="input-text"
                  :useGrouping="false"
                  @keydown.enter="payEMI"
                ></InputNumber>
              </div>
              <CustomButton title="Pay Loan" icon="indian-rupee" @click="payEMI" />
            </div>
          </div>
        </Transition>
      </template>
    </Card>
    <ConfirmDialog :draggable="false" :closable="false" />
  </main>
</template>
<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.25s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

h1 {
  margin: 0.5rem;
}

h2 {
  margin: 0;
}

.details-container {
  display: grid;
  column-gap: 2rem;
  grid-template-columns: 1fr 1fr;
  text-align: start;

  .details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1rem;
  }
}
</style>
